import { useCallback, useState } from "react";
import { RefreshCwIcon, CheckIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useUploadedVideo, useUploadVideo } from "@/tanstack-hooks";
import { UserGender } from "@/generated-api/models/UserGender";
import SpinnerSmall from "../SpinnerSmall";
import { useTranslation } from "@/hooks/useTranslation";
import AutoRotateVideo from "./AutoRotateVideo";
import VideoRecorder from "./VideoRecorder";

const getVideoSeconds = (gender: UserGender | null): number => {
  switch (gender) {
    case UserGender.FEMALE:
      return 6;
    case UserGender.MALE:
      return 4;
    default:
      return 6;
  }
};

export default function VideoCapture({
  gender,
  existingVideoUrl,
}: {
  gender: UserGender | null;
  existingVideoUrl: string | null;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const uploadVideoMutation = useUploadVideo();
  const skipUseUploadedVideo = useUploadedVideo();

  const [isReviewing, setIsReviewing] = useState<boolean>(
    existingVideoUrl !== null,
  );
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [needsRotation, setNeedsRotation] = useState(false);

  const handleUpload = useCallback(() => {
    if (recordedVideo) {
      const ext = "." + recordedVideo.type.split("/")[1];
      const uploadInfo = {
        sizeBytes: recordedVideo.size,
        sizeMB: +(recordedVideo.size / (1024 * 1024)).toFixed(2),
        type: recordedVideo.type,
        extension: ext,
      };
      console.log("Uploading verification video", uploadInfo);
      uploadVideoMutation.mutate(
        {
          video: recordedVideo,
          extension: ext,
          needs_rotation: needsRotation,
        },
        {
          onError: (error) => {
            console.log(
              "Error uploading verification video",
              uploadInfo,
              error,
            );
            toast({
              title: t.FACE_VERIFICATION.ERRORS.UPLOAD,
              description: t.SHARED.ERRORS.PERSISTENT,
              variant: "destructive",
              className: "dark",
            });
          },
          onSettled: () => {
            setRecordedVideo(null);
            // setIsReviewing(false);
          },
        },
      );
    }
  }, [recordedVideo, toast, uploadVideoMutation, t]);

  function handleSkipUpload() {
    skipUseUploadedVideo.mutate(
      {},
      {
        onError: (error) => {
          console.log(error);
          toast({
            title: t.FACE_VERIFICATION.ERRORS.EXISTING,
            description: t.SHARED.ERRORS.PERSISTENT,
            variant: "destructive",
            className: "dark",
          });
        },
        onSettled: () => {
          setRecordedVideo(null);
        },
      },
    );
  }

  const handleRetake = useCallback(() => {
    setIsReviewing(false);
    setRecordedVideo(null);
  }, []);

  return (
    <div className="max-w-md mx-auto pt-0 px-4 shadow-md rounded-lg">
      {isReviewing ? (
        <>
          <AutoRotateVideo
            src={
              recordedVideo
                ? URL.createObjectURL(recordedVideo)
                : existingVideoUrl || ""
            }
            isExistingVideo={existingVideoUrl ? true : false}
            setNeedsRotation={setNeedsRotation}
          />
          <div className="flex justify-center mb-4">
            <div className="w-full grid grid-cols-2 gap-4">
              <Button
                onClick={handleRetake}
                variant="secondary"
                aria-label="Retake video"
                className="w-full"
                disabled={uploadVideoMutation.isPending}
              >
                <RefreshCwIcon className="mr-2 h-4 w-4" />
                {t.FACE_VERIFICATION.LABELS.RETAKE_BUTTON}
              </Button>
              {recordedVideo ? (
                <Button
                  onClick={handleUpload}
                  aria-label="Confirm and upload video"
                  className="w-full"
                  disabled={uploadVideoMutation.isPending}
                >
                  {uploadVideoMutation.isPending ? (
                    <SpinnerSmall black={true} />
                  ) : (
                    <>
                      <CheckIcon className="mr-2 h-4 w-4" />
                      {t.SHARED.LABELS.CONFIRM_BUTTON}
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleSkipUpload}
                  className="w-full"
                  disabled={skipUseUploadedVideo.isPending}
                >
                  <CheckIcon className="mr-2 h-4 w-4" />
                  {t.SHARED.LABELS.CONFIRM_BUTTON}
                </Button>
              )}
            </div>
          </div>
        </>
      ) : (
        <VideoRecorder
          videoSeconds={getVideoSeconds(gender)}
          onComplete={(blob) => {
            setRecordedVideo(blob);
            setIsReviewing(true);
          }}
        />
      )}
    </div>
  );
}
